import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export const WEBSITE_ID_STORAGE_KEY = "zenyt-v2-website-id";

// Legacy placeholder values that used to be hard-coded as fallbacks. Treat
// them as "no selection" so the bootstrap hook can pick a real website
// from the user's accessible list instead of pinning them to a dead ID.
const PLACEHOLDER_WEBSITE_IDS = new Set(["", "website-demo"]);

export function readStoredWebsiteId(): string | null {
  try {
    const value = localStorage.getItem(WEBSITE_ID_STORAGE_KEY);
    if (!value || PLACEHOLDER_WEBSITE_IDS.has(value)) return null;
    return value;
  } catch {
    return null;
  }
}

export function writeStoredWebsiteId(websiteId: string | null) {
  try {
    if (websiteId && !PLACEHOLDER_WEBSITE_IDS.has(websiteId)) {
      localStorage.setItem(WEBSITE_ID_STORAGE_KEY, websiteId);
    } else {
      localStorage.removeItem(WEBSITE_ID_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode
  }
}

export interface AuthState {
  accessToken: string | null;
  websiteId: string | null;
}

const initialState: AuthState = {
  accessToken: null,
  // Default to null so `useActiveWebsiteBootstrap` runs its first branch and
  // picks a real website from the user's `/websites?has_findings=true` list
  // on first load. An explicit env override is still honored for demo builds.
  websiteId: readStoredWebsiteId() ?? import.meta.env.VITE_DEMO_WEBSITE_ID ?? null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials(
      state,
      action: PayloadAction<{ accessToken: string; websiteId?: string | null }>,
    ) {
      state.accessToken = action.payload.accessToken;
      if (action.payload.websiteId !== undefined) {
        state.websiteId = action.payload.websiteId;
        writeStoredWebsiteId(action.payload.websiteId);
      }
    },
    setWebsiteId(state, action: PayloadAction<string | null>) {
      state.websiteId = action.payload;
      writeStoredWebsiteId(action.payload);
    },
    clearCredentials(state) {
      state.accessToken = null;
      state.websiteId = null;
      writeStoredWebsiteId(null);
    },
  },
});

export const { setCredentials, setWebsiteId, clearCredentials } = authSlice.actions;
export const authReducer = authSlice.reducer;

export const selectAccessToken = (state: { auth: AuthState }) => state.auth.accessToken;
export const selectWebsiteId = (state: { auth: AuthState }) => state.auth.websiteId;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  Boolean(state.auth.accessToken);
